/**
 * Global DataGrid Column Width Manager
 * Manages column widths across all DataGrids in the application
 * Preserves user-resized column widths for the entire session
 */

interface ColumnWidthData {
  [gridId: string]: {
    [columnIndex: number]: number;
  };
}

class DataGridColumnWidthManager {
  private static instance: DataGridColumnWidthManager;
  private readonly STORAGE_KEY = 'dataGridColumnWidths';

  private constructor() {}

  public static getInstance(): DataGridColumnWidthManager {
    if (!DataGridColumnWidthManager.instance) {
      DataGridColumnWidthManager.instance = new DataGridColumnWidthManager();
    }
    return DataGridColumnWidthManager.instance;
  }

  /**
   * Get stored column widths for a specific grid
   */
  public getColumnWidths(gridId: string): { [columnIndex: number]: number } | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      const allWidths: ColumnWidthData = JSON.parse(stored);
      return allWidths[gridId] || null;
    } catch (error) {
      console.warn('Failed to retrieve column widths:', error);
      return null;
    }
  }

  /**
   * Save column widths for a specific grid
   */
  public saveColumnWidths(gridId: string, columnWidths: { [columnIndex: number]: number }): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      const allWidths: ColumnWidthData = stored ? JSON.parse(stored) : {};
      
      allWidths[gridId] = columnWidths;
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(allWidths));
    } catch (error) {
      console.warn('Failed to save column widths:', error);
    }
  }

  /**
   * Update a specific column width for a grid
   */
  public updateColumnWidth(gridId: string, columnIndex: number, width: number): void {
    const currentWidths = this.getColumnWidths(gridId) || {};
    currentWidths[columnIndex] = width;
    this.saveColumnWidths(gridId, currentWidths);
  }

  /**
   * Clear all stored column widths (for session reset)
   */
  public clearAllWidths(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear column widths:', error);
    }
  }

  /**
   * Clear column widths for a specific grid
   */
  public clearGridWidths(gridId: string): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;
      
      const allWidths: ColumnWidthData = JSON.parse(stored);
      delete allWidths[gridId];
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(allWidths));
    } catch (error) {
      console.warn('Failed to clear grid column widths:', error);
    }
  }
}

export default DataGridColumnWidthManager;